import { Compass } from "lucide-react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { LegacySettingsTab } from "@/routes/LegacySettingsTab";
import { AccountSettingsPage } from "@/features/account/AccountSettingsPage";
import { HelpCenterPage } from "@/features/account/HelpCenterPage";
import { SectionPlaceholder } from "@/shared/components/SectionPlaceholder";
import { AdminHome, AdminLayout } from "@/routes/AdminLayout";
import { CompaniesRoute } from "@/routes/CompaniesRoute";
import { DashboardRoute } from "@/routes/DashboardRoute";
import { HomeRoute } from "@/routes/HomeRoute";
import { RootLayout } from "@/routes/RootLayout";
import { AiModelsAdminRoute } from "@/routes/admin/AiModelsAdminRoute";
import { AiUsageRoute } from "@/routes/admin/AiUsageRoute";
import { CenaAdminRoute } from "@/routes/admin/CenaAdminRoute";
import { ConfigAuditRoute } from "@/routes/admin/ConfigAuditRoute";
import { CopilotoAdminRoute } from "@/routes/admin/CopilotoAdminRoute";
import { CompanyGroupsAdminRoute } from "@/routes/admin/CompanyGroupsAdminRoute";
import { UsersAdminRoute } from "@/routes/admin/UsersAdminRoute";
import { PromptDefaultsRoute } from "@/routes/admin/PromptDefaultsRoute";
import { UploadTemplatesRoute } from "@/routes/admin/UploadTemplatesRoute";
import { CompanyDashboardRoute } from "@/routes/company/CompanyDashboardRoute";
import { MentionsRoute } from "@/routes/company/MentionsRoute";
import { OverviewRoute } from "@/routes/company/OverviewRoute";
import { ConfigAssistantRoute } from "@/routes/company/ConfigAssistantRoute";
import { SettingsRoute } from "@/routes/company/SettingsRoute";
import { SocialReportRoute } from "@/routes/company/SocialReportRoute";
import { UploadDetailRoute } from "@/routes/company/UploadDetailRoute";
import { UploadsRoute } from "@/routes/company/UploadsRoute";
import { DownloadsRoute } from "@/routes/company/DownloadsRoute";
import { TrashRoute } from "@/routes/company/TrashRoute";
import { WhyRoute } from "@/routes/company/WhyRoute";
import { EditHistoryRoute } from "@/routes/company/EditHistoryRoute";
import { ReprocessRunDetailRoute } from "@/routes/company/ReprocessRunDetailRoute";
import { ReprocessRunsRoute } from "@/routes/company/ReprocessRunsRoute";
// Ponto de encaixe do Versa (módulo removível — ver versa/ARCHITECTURE.md).
import { versaCompanyRoutes, versaPublicRoutes } from "@/features/versa/routes";

/**
 * A volta do Keycloak (`?code=...&state=...`).
 *
 * O `AppShellProvider` intercepta antes de qualquer render: troca o
 * código por tokens, limpa a URL e leva pra rota padrão. Esta entrada
 * existe só pra o roteador resolver o caminho em vez de cair no 404, e
 * é ela que o realm precisa ter em `redirectUris`.
 *
 * Substituiu o callback do Google, que era do backend descartado — ele
 * lia tokens do FRAGMENTO da URL, e o fluxo novo usa a query, porque o
 * código de autorização precisa chegar ao servidor de token e o
 * fragmento nunca sai do navegador.
 */
function AuthCallbackRoute() {
  return null;
}

function NotFoundRoute() {
  return (
    <SectionPlaceholder
      icon={Compass}
      title="Página não encontrada"
      description="O endereço acessado não existe. Use o menu lateral para voltar a uma seção conhecida."
      badge={false}
    />
  );
}

export const router = createBrowserRouter([
  // Ponto de encaixe do Versa: o link do cliente, fora da casca (vazio com
  // ele desligado).
  ...versaPublicRoutes,
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <HomeRoute /> },
      { path: "empresas", element: <CompaniesRoute /> },
      { path: "dashboard", element: <DashboardRoute /> },
      // A Mestra Help (ex-Copiloto) mora no balão da topbar desde
      // 2026-10-13; link antigo pra tela cheia volta pro início.
      { path: "copiloto", element: <Navigate to="/" replace /> },
      { path: "configuracoes", element: <AccountSettingsPage /> },
      // Perfil, Preferências e Segurança viraram seções de Configurações
      // (2026-10-15); os links antigos caem na seção certa.
      { path: "perfil", element: <Navigate to="/configuracoes?aba=perfil" replace /> },
      { path: "preferencias", element: <Navigate to="/configuracoes?aba=preferencias" replace /> },
      { path: "seguranca", element: <Navigate to="/configuracoes?aba=seguranca" replace /> },
      { path: "ajuda", element: <HelpCenterPage /> },
      { path: "auth/callback", element: <AuthCallbackRoute /> },

      {
        path: "admin",
        element: <AdminLayout />,
        children: [
          {
            // A landing da Administração: um atalho por seção (2026-10-15).
            index: true,
            element: <AdminHome />
          },
          { path: "copiloto", element: <CopilotoAdminRoute /> },
          { path: "usuarios", element: <UsersAdminRoute /> },
          { path: "modelos-ia", element: <AiModelsAdminRoute /> },
          { path: "consumo", element: <AiUsageRoute /> },
          { path: "templates-upload", element: <UploadTemplatesRoute /> },
          { path: "prompts-padrao", element: <PromptDefaultsRoute /> },
          {
            path: "cena",
            element: <CenaAdminRoute />
          },
          { path: "nucleos", element: <CompanyGroupsAdminRoute /> },
          { path: "auditoria", element: <ConfigAuditRoute /> }
        ]
      },

      {
        path: "empresas/:companyId",
        children: [
          // A raiz da empresa não tem tela: sem este redirect o React
          // Router renderizava um `<Outlet>` vazio e a pessoa via uma
          // página em branco (2026-10-09). `replace` pra o botão de voltar
          // não cair de novo na raiz.
          { index: true, element: <Navigate to="visao-geral" replace /> },
          { path: "visao-geral", element: <OverviewRoute /> },
          { path: "dashboard", element: <CompanyDashboardRoute /> },
          { path: "mencoes", element: <MentionsRoute /> },
          { path: "justificativas", element: <WhyRoute /> },
          { path: "lixeira", element: <TrashRoute /> },
          { path: "historico-edicoes", element: <EditHistoryRoute /> },
          { path: "uploads", element: <UploadsRoute /> },
          { path: "uploads/:uploadId", element: <UploadDetailRoute /> },
          { path: "reprocessamentos", element: <ReprocessRunsRoute /> },
          { path: "reprocessamentos/:runId", element: <ReprocessRunDetailRoute /> },
          { path: "relatorios/cena", element: <SocialReportRoute /> },
          { path: "downloads", element: <DownloadsRoute /> },
          // Ponto de encaixe do Versa (vazio com ele desligado).
          ...versaCompanyRoutes,
          { path: "configuracoes", element: <SettingsRoute /> },
          { path: "configuracoes/assistente", element: <ConfigAssistantRoute /> },
          {
            path: "configuracoes/concorrentes",
            element: <LegacySettingsTab tab="concorrentes" />
          },
          {
            path: "configuracoes/ia/temas",
            element: <LegacySettingsTab tab="temas" />
          },
          {
            path: "configuracoes/ia/canais",
            element: <LegacySettingsTab tab="canais" />
          },
          {
            path: "configuracoes/ia/veiculos",
            element: <LegacySettingsTab tab="veiculos" />
          },
          {
            path: "configuracoes/ia/porta-vozes",
            element: <LegacySettingsTab tab="portavozes" />
          },
          {
            path: "configuracoes/ia/publicadores",
            element: <LegacySettingsTab tab="publicadores" />
          },
          {
            path: "configuracoes/ia/clipadoras",
            element: <LegacySettingsTab tab="clipadoras" />
          },
          {
            path: "configuracoes/ia/mensagens-chave",
            element: <LegacySettingsTab tab="mensagens" />
          },
          {
            path: "configuracoes/ia/prompts",
            element: <LegacySettingsTab tab="premissas" />
          }
        ]
      },

      { path: "*", element: <NotFoundRoute /> }
    ]
  }
]);
